import { useEffect } from 'react';
import { GTM_ID, GTM_ENABLED, IS_TEST, strings } from './constants';
import { getOrderCookieData } from './utils/getOrderCookieData';
import { getUrlParam, capitalizeFirstLetter } from './utils';
import { getPriceByPacket, getRangeData } from './Domain/PriceTable';
import { isNewCampaignActivated, CAMPAIGN_PACKET } from './Domain/NewCampaign';
import { getPacketName } from './Domain/NetvisorPacket';

declare global {
  interface Window {
    dataLayer: any[];
  }
}

// GTM event names
const EVENT_VIEW_ITEM = 'view_item';
const EVENT_VIRTUAL_PAGE_VIEW = 'virtual_page_view';
const EVENT_COMPANY_ID_ENTERED = 'company_id_entered';
const EVENT_PRODUCT_UPDATED = 'product_updated';
const EVENT_PURCHASE = 'purchase';

// DataLayer field names
const FIELD_EVENT = 'event';
const FIELD_ECOMMERCE = 'ecommerce';
const FIELD_PAGE_PATH = 'page_path';
const FIELD_PAGE_TITLE = 'page_title';
const FIELD_TURNOVER = 'turnover';
const FIELD_TRANSACTION_ID = 'transaction_id';
const FIELD_VALUE = 'value';
const FIELD_CURRENCY = 'currency';
const FIELD_ITEMS = 'items';
const FIELD_ITEM_ID = 'item_id';
const FIELD_ITEM_NAME = 'item_name';
const FIELD_ITEM_VARIANT = 'item_variant';
const FIELD_PRICE = 'price';
const FIELD_QUANTITY = 'quantity';
const FIELD_ENHANCED_CONVERSION_DATA = 'enhanced_conversion_data';
const FIELD_PREVIOUS_ITEM_ID = 'previous_item_id';
const FIELD_NEW_ITEM_ID = 'new_item_id';

const VALUE_ID = 'online';
const VALUE_NAME = 'Online Purchase – Standard';
const VALUE_CAMPAIGN_ID = 'online_newcompanycampaign';
const VALUE_CAMPAIGN_NAME = 'Online Purchase – New Company Campaign';
const VALUE_PAGE_PATH = '/online/start';
const VALUE_PAGE_TITLE = 'Online – Start';
const VALUE_PAGE_PATH_FORM = '/online/form';
const VALUE_PAGE_TITLE_FORM = 'Online – Form Opened';
const VALUE_CAMPAIGN_PAGE_PATH_FORM = '/online/form-newcompanycampaign';
const VALUE_CAMPAIGN_PAGE_TITLE_FORM = 'Online – Form Opened (New Company Campaign)';
const VALUE_PAGE_PATH_CONFIRMATION = '/online/confirmation';
const VALUE_PAGE_TITLE_CONFIRMATION = 'Online – Confirmation';
const VALUE_CAMPAIGN_PAGE_PATH_CONFIRMATION = '/online/confirmation-newcompanycampaign';
const VALUE_CAMPAIGN_PAGE_TITLE_CONFIRMATION = 'Online – Confirmation (New Company Campaign)';

export const LoadGoogleAnalytics = () => {
  useEffect(() => {
    if (!GTM_ENABLED) {
      return;
    }

    // Check if GTM is already loaded to prevent duplicates
    if (window.dataLayer && document.querySelector(`script[src*="googletagmanager.com/gtm.js"]`)) {
      return;
    }
    
    // Add GTM script to head (this initializes dataLayer automatically)
    addGTMScript(GTM_ID);
    
    // Add noscript fallback to body
    addGTMNoScript(GTM_ID);
    
  }, []);

  return null;
};

// Add GTM script to head using the official GTM script string
const addGTMScript = (gtmId: string) => {
  const gtmScript = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');`;

  const script = document.createElement('script');
  script.innerHTML = gtmScript;
  
  // Insert at beginning of head (GTM recommendation)
  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
};

// Add GTM noscript iframe to body
const addGTMNoScript = (gtmId: string) => {
  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  
  noscript.appendChild(iframe);
  
  // Insert at beginning of body (GTM recommendation)
  if (document.body.firstChild) {
    document.body.insertBefore(noscript, document.body.firstChild);
  } else {
    document.body.appendChild(noscript);
  }
};

async function safeHash(value: string | null): Promise<string | null> {
  try {
    if (!value) return null;
    const encoder = new TextEncoder();
    const data = encoder.encode(value.trim().toLowerCase());
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (e) {
    return null;
  }
}

// Clear dataLayer variables to prevent unwanted inheritance
const clearDataLayerFields = () => ({
  [FIELD_ECOMMERCE]: undefined,
  [FIELD_PAGE_PATH]: undefined,
  [FIELD_PAGE_TITLE]: undefined,
  [FIELD_TURNOVER]: undefined,
  [FIELD_ENHANCED_CONVERSION_DATA]: undefined,
  [FIELD_PREVIOUS_ITEM_ID]: undefined,
  [FIELD_NEW_ITEM_ID]: undefined
});

// Private helper to send virtual page view events
function sendVirtualPageView(pagePath: string, pageTitle: string) {
  sendEvent(EVENT_VIRTUAL_PAGE_VIEW, {
    [FIELD_PAGE_PATH]: pagePath,
    [FIELD_PAGE_TITLE]: pageTitle
  });
}

// Private helper to send view item events
function sendViewItem(itemId: string, itemName: string, itemVariant: string, price: number) {
  sendEvent(EVENT_VIEW_ITEM, {
    [FIELD_ECOMMERCE]: {
      [FIELD_ITEMS]: [
        {
          [FIELD_ITEM_ID]: itemId,
          [FIELD_ITEM_NAME]: itemName,
          [FIELD_ITEM_VARIANT]: itemVariant,
          [FIELD_PRICE]: price,
          [FIELD_QUANTITY]: 1
        }
      ]
    },
    [FIELD_TURNOVER]: getRangeData(Number(getUrlParam("turnover")))?.nvFormat ?? ""
  });
}

export const sendEvent = (eventName: string, data: any = {}) => {
  if (!GTM_ENABLED) {
    return;
  }

  if (window.dataLayer) {
    // Clear all fields first as separate push
    window.dataLayer.push(clearDataLayerFields());
    
    // Then send the actual event
    window.dataLayer.push({
      [FIELD_EVENT]: eventName,
      ...data
    });
    
    if (IS_TEST) {
      console.log('GTM Event sent:', eventName, data);
    }
  }
};

// Send GTM purchase completion event
export async function sendGAPurchase() {
  if (!GTM_ENABLED) {
    return;
  }

  const sessionPurchaseInfo = sessionStorage.getItem("purchaseInfo");
  const purchaseInfo = JSON.parse(sessionPurchaseInfo || "{}");
  
  const orderId = sessionStorage.getItem(strings.ORDER_ID_COOKIE_NAME) || "";
  const { decodedOrderData } = getOrderCookieData();
  
  // Use persisted campaign info from cookie data instead of current URL state
  const isNewCampaign = decodedOrderData?.IsNewCompanyCampaign || false;

  let enhancedData: any = {};
  try {
    if (decodedOrderData) {
      const [emailHash, firstNameHash, lastNameHash, phoneHash] = await Promise.all([
        safeHash(decodedOrderData.Email),
        safeHash(decodedOrderData.FirstName),
        safeHash(decodedOrderData.LastName),
        safeHash(decodedOrderData.PhoneNumber)
      ]);

      if (emailHash) enhancedData.email = emailHash;
      if (firstNameHash) enhancedData.first_name = firstNameHash;
      if (lastNameHash) enhancedData.last_name = lastNameHash;
      if (phoneHash) enhancedData.phone_number = phoneHash;
    }
  } catch (error) {
    enhancedData = {};
  }

  const purchaseEventData: any = {
    [FIELD_ECOMMERCE]: {
      [FIELD_TRANSACTION_ID]: orderId,
      [FIELD_VALUE]: purchaseInfo.subTotal * 12, //value is 12 x price because we want the annual value
      [FIELD_CURRENCY]: 'EUR',
      [FIELD_ITEMS]: [
        {
          [FIELD_ITEM_ID]: isNewCampaign ? VALUE_CAMPAIGN_ID : VALUE_ID,
          [FIELD_ITEM_NAME]: isNewCampaign ? VALUE_CAMPAIGN_NAME : VALUE_NAME,
          [FIELD_ITEM_VARIANT]: capitalizeFirstLetter(purchaseInfo.packet),
          [FIELD_PRICE]: purchaseInfo.subTotal,
          [FIELD_QUANTITY]: 1
        }
      ]
    }
  };

  // Add enhanced conversions data if available
  if (Object.keys(enhancedData).length > 0) {
    purchaseEventData[FIELD_ENHANCED_CONVERSION_DATA] = enhancedData;
  }

  sendEvent(EVENT_PURCHASE, purchaseEventData);
  
  sendVirtualPageView(
    isNewCampaign ? VALUE_CAMPAIGN_PAGE_PATH_CONFIRMATION : VALUE_PAGE_PATH_CONFIRMATION,
    isNewCampaign ? VALUE_CAMPAIGN_PAGE_TITLE_CONFIRMATION : VALUE_PAGE_TITLE_CONFIRMATION
  );
}

// Send GTM events when business ID is entered
export function sendGABusinessID() {
  if (!GTM_ENABLED) {
    return;
  }

  const isNewCampaign = isNewCampaignActivated();
  
  sendEvent(EVENT_COMPANY_ID_ENTERED, {});
  sendVirtualPageView(
    isNewCampaign ? VALUE_CAMPAIGN_PAGE_PATH_FORM : VALUE_PAGE_PATH_FORM,
    isNewCampaign ? VALUE_CAMPAIGN_PAGE_TITLE_FORM : VALUE_PAGE_TITLE_FORM
  );
}

// Send GTM events when campaign is selected
export function sendGACampaignSelect(prices: any) {
  if (!GTM_ENABLED) {
    return;
  }

  sendEvent(EVENT_PRODUCT_UPDATED, {
    [FIELD_PREVIOUS_ITEM_ID]: VALUE_ID,
    [FIELD_NEW_ITEM_ID]: VALUE_CAMPAIGN_ID
  });

  sendViewItem(
    VALUE_CAMPAIGN_ID,
    VALUE_CAMPAIGN_NAME,
    capitalizeFirstLetter(CAMPAIGN_PACKET),
    getPriceByPacket(prices, CAMPAIGN_PACKET)
  );
  
  sendVirtualPageView(
    VALUE_CAMPAIGN_PAGE_PATH_FORM,
    VALUE_CAMPAIGN_PAGE_TITLE_FORM
  );
}

// Send GTM events when product page is loaded
export function sendGAProductView(prices: any) {
  if (!GTM_ENABLED) {
    return;
  }
  
  const packetName = getPacketName();
  
  sendViewItem(
    VALUE_ID,
    VALUE_NAME,
    packetName?.toString() || '',
    getPriceByPacket(prices, packetName?.toString() || null)
  );
  
  sendVirtualPageView(
    VALUE_PAGE_PATH,
    VALUE_PAGE_TITLE
  );
}
